import { Alert, Box, SideSheet, Skeleton, Text } from '@digibee/beehive-ui';
import { OpenNewWindow } from 'iconoir-react';
import head from 'lodash/head';
import moment from 'moment';
import { Link, LinkProps, useParams } from 'react-router-dom';
import styled from 'styled-components';

import CustomAlertV2 from './components/CustomAlert/CustomAlertV2';
import PipelineDeployWithServerless from '../PipelineDeploy/PipelineDeployWithServerless';
import TriggerDetails from '../TriggerDetails/TriggerDetails';

import i18n from '~/common/helpers/i18n';
import { buildPipelineLogsUrl } from '~/common/helpers/url/monitorPaths';
import {
  DeploymentIssueInput,
  getDeploymentIssues
} from '~/components/DeploymentIssueIndicator';
import { Deployment } from '~/entities/Deployment';
import { Trigger } from '~/entities/Trigger';

interface DetailsDeploymentWithServerlessProps {
  onCancel: (value?: boolean) => void;
  title?: string;
  deployment: Deployment;
  trigger: Trigger;
  linkGoToPipeline: string;
  // An array: `mapValueSchema` builds one row per trigger property.
  triggerSource?: { label: string; value: string }[];
  redeploy: (deployment: Deployment['pipeline']) => void;
}

const getMessage = (deployment: Deployment) => {
  if (deployment?.lastError || deployment?.lastOMM) {
    return ` - ${moment(
      deployment?.lastError || deployment?.lastOMM,
      'x'
    ).format('DD/MM/YYYY HH:mm:ss')}`;
  }
  return '';
};

const WrapperTrigger = styled.div`
  width: 10%;
  display: flex;
  width: 45px;
  height: 45px;
`;

const TriggerImage = styled.img`
  user-drag: none;
  user-select: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.colors.gra25};
`;

const LinkPipeline = styled((props: LinkProps) => <Link {...props} />)`
  display: flex;
  text-decoration: none;
  p {
    padding-right: 8px;
  }
  color: ${props => props.theme.colors.pri500};
`;

const DetailsDeploymentWithServerless = ({
  onCancel,
  deployment,
  trigger,
  linkGoToPipeline,
  triggerSource,
  redeploy
}: DetailsDeploymentWithServerlessProps) => {
  const { realm, environment } = useParams();

  // Same rules that count the problems on the listing indicator. They used to be their own set
  // here, and the two disagreed on order and on which problems existed at all. The listing raises
  // a subset of these — errors are this sidesheet's alone — but never a problem the sidesheet
  // would then deny.
  const issueKinds = new Set(
    getDeploymentIssues(deployment as DeploymentIssueInput).map(
      issue => issue.kind
    )
  );
  // The status chip labels a degraded deployment "Starting", so without an alert of its own the
  // problem is invisible here too. Docs: the replicas did not come up in time — redeploy.
  const isDegraded = issueKinds.has('statusAlert');
  const hasErrors = issueKinds.has('errorsAndIssues');

  const onRedeploy = () => {
    onCancel();
    redeploy(deployment?.pipeline);
  };

  return (
    <SideSheet
      open
      onClose={() => onCancel(false)}
      header={i18n.t('scenes.runtime.labels.details_deployment')}
      size='small'
      actions={
        <>
          <SideSheet.Actions>
            <LinkPipeline
              target='_blank'
              to={`${linkGoToPipeline}`}
              data-testid='run-page-detail-open-new-pipeline'
            >
              <OpenNewWindow className='icon-smallest' />
            </LinkPipeline>
          </SideSheet.Actions>
        </>
      }
    >
      <PipelineDeployWithServerless deployment={deployment} />
      <SideSheet.Section data-testid='run-page-detail'>
        <Box>
          {!trigger ? (
            <Skeleton variant='circular' />
          ) : (
            <Box css={{ width: 'auto' }}>
              <Box
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}
              >
                <Box css={{ display: 'flex', width: 45, height: 45 }}>
                  <WrapperTrigger>
                    <TriggerImage
                      title={
                        i18n.t('scenes.beta_pipelines.labels.trigger_icon') ||
                        ''
                      }
                      src={`${trigger.iconURL}`}
                    />
                  </WrapperTrigger>
                </Box>
                <Text css={{ paddingLeft: '$2' }} variant='b3'>
                  {deployment?.pipeline?.description}
                </Text>
              </Box>
              <Box css={{ marginTop: '$1' }}>
                <TriggerDetails
                  source={triggerSource ?? []}
                  // @ts-expect-error
                  title={head(trigger?.jsonSchema)?.label}
                  // @ts-expect-error
                  triggers={deployment?.trigger?.data}
                />
              </Box>
            </Box>
          )}
        </Box>
      </SideSheet.Section>
      {(issueKinds.size > 0 || deployment?.pipelineUpdateProject) && (
        <SideSheet.Section>
          <Box>
            {issueKinds.has('outOfMemory') && (
              <Box>
                <Alert
                  title={`Out of Memory ${getMessage(deployment)}`}
                  collapsible
                  variant='warning'
                  content={
                    i18n.t('scenes.runtime.messages.error.out_of_memory') || ''
                  }
                />
              </Box>
            )}
            {issueKinds.has('outdatedGlobal') && (
              <Box>
                <Alert
                  title={
                    i18n.t('label.global_has_been_edited_details_title_msg') ||
                    ''
                  }
                  collapsible
                  variant='warning'
                  // @ts-expect-error
                  content={
                    <CustomAlertV2
                      text={
                        <>
                          {i18n.t(
                            'label.update_globals_redeploy_needed_card_details_msg_warning'
                          ) || ''}
                          <br />
                          <a
                            href={
                              i18n.t('action.learn_more_about_globals_url') ||
                              ''
                            }
                            data-testid='run-detail-link-recycled'
                            target='_blank'
                            rel='noreferrer'
                          >
                            {i18n.t(
                              'action.learn_more_about_globals_warning_link'
                            ) || ''}
                          </a>
                        </>
                      }
                      textButton={i18n.t('action.redeploy_pipeline') || ''}
                      onClick={onRedeploy}
                    />
                  }
                />
              </Box>
            )}
            {issueKinds.has('outdatedAccount') && (
              <Box>
                <Alert
                  title={i18n.t('label.edited_account') || ''}
                  collapsible
                  variant='warning'
                  // @ts-expect-error
                  content={
                    <CustomAlertV2
                      text={
                        <>
                          {i18n.t(
                            'label.update_accounts_redeploy_needed_card_details_msg_warning'
                          ) || ''}
                          <br />
                          <a
                            href={
                              i18n.t(
                                'action.learn_more_about_accounts_warning_url'
                              ) || ''
                            }
                            data-testid='run-detail-link-recycled'
                            target='_blank'
                            rel='noreferrer'
                          >
                            {i18n.t(
                              'action.learn_more_about_accounts_warning_link'
                            ) || ''}
                          </a>
                        </>
                      }
                      textButton={i18n.t('action.redeploy_pipeline') || ''}
                      onClick={onRedeploy}
                    />
                  }
                />
              </Box>
            )}
            {isDegraded && (
              <Box>
                <Alert
                  title={i18n.t('common.labels.degraded') || ''}
                  collapsible
                  variant='warning'
                  // @ts-expect-error
                  content={
                    <CustomAlertV2
                      text={
                        <>
                          {i18n.t('scenes.runtime.messages.error.degraded') ||
                            ''}
                          <br />
                          <a
                            href={
                              i18n.t(
                                'action.learn_more_about_deployment_status_url'
                              ) || ''
                            }
                            target='_blank'
                            rel='noreferrer'
                          >
                            {i18n.t(
                              'action.learn_more_about_deployment_status_link'
                            ) || ''}
                          </a>
                        </>
                      }
                      textButton={i18n.t('action.redeploy_pipeline') || ''}
                      onClick={onRedeploy}
                    />
                  }
                />
              </Box>
            )}
            {hasErrors && (
              <Box>
                <Alert
                  // Short title, explanation in the collapsed body — the same shape the
                  // globals and accounts alerts below use. It used to carry the whole
                  // sentence plus a "Learn more here." that pointed at nothing.
                  title={i18n.t('label.error') || ''}
                  collapsible
                  variant='danger'
                  // @ts-expect-error
                  content={
                    <CustomAlertV2
                      text={
                        <>
                          {i18n.t(
                            'label.pipeline_has_errors_issues_generic_mixed_warning_error_msg'
                          ) || ''}{' '}
                          {i18n.t('label.error-message-pipeline-runtime') || ''}
                          <Link
                            target='_blank'
                            data-testid='monitor-completed-executions-button-executions-details-see-all-logs'
                            to={
                              deployment?.pipeline.name
                                ? buildPipelineLogsUrl(
                                    realm,
                                    environment,
                                    deployment.pipeline.name,
                                    '1 Hour'
                                  )
                                : '#'
                            }
                          >
                            {i18n.t(
                              'scenes.execution_finished.actions.see_all_logs'
                            ) || ''}
                          </Link>
                        </>
                      }
                    />
                  }
                />
              </Box>
            )}
            {deployment?.pipelineUpdateProject && (
              <Box>
                <Alert
                  title={`${i18n.t('label.needs_redeploy_alert') || ''}`}
                  collapsible
                  variant='warning'
                  // @ts-expect-error
                  content={
                    <CustomAlertV2
                      text={i18n.t('label.needs_redeploy_msg') || ''}
                      textButton={i18n.t('action.redeploy_pipeline') || ''}
                      onClick={onRedeploy}
                    />
                  }
                />
              </Box>
            )}
          </Box>
        </SideSheet.Section>
      )}
    </SideSheet>
  );
};
export default DetailsDeploymentWithServerless;
